import React from 'react';
import { useObjetPhotoPreview } from '../hooks/useObjets';
import { usePhotoViewer } from '../contexts/PhotoViewerContext';
import { buildPhotoViewerItemsFromObjet } from '../utils/photoMedia';
import type { PhotoMediaSize } from '../utils/photoMedia';

function hasPhotoMeta(objet: { photoCount?: number; photos?: Array<{ url?: string; s3Key?: string }> }) {
  return (objet.photoCount ?? objet.photos?.length ?? 0) > 0;
}

function browserPhotoUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('data:')) return url;
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname === 'minio' || hostname.startsWith('minio.')) {
      return undefined;
    }
  } catch {
    return url;
  }
  return url;
}

interface ObjetPhotoThumbProps {
  objet: {
    id: string;
    photoCount?: number;
    photos?: Array<{ id?: string; url?: string; s3Key?: string }>;
    nom?: string;
  };
  className?: string;
  placeholder?: string;
  imgClassName?: string;
  size?: PhotoMediaSize;
  clickable?: boolean;
  onPhotoClick?: (event: React.MouseEvent) => void;
}

const ObjetPhotoThumb: React.FC<ObjetPhotoThumbProps> = ({
  objet,
  className = 'w-14 h-14',
  placeholder = '📦',
  imgClassName = 'w-full h-full object-cover',
  size = 'thumb',
  clickable = true,
  onPhotoClick,
}) => {
  const { openPhotoViewer } = usePhotoViewer();
  const inlineUrl = browserPhotoUrl(objet.photos?.[0]?.url);
  const needsPreview = hasPhotoMeta(objet) && !inlineUrl;
  const { data: previewUrl, isLoading } = useObjetPhotoPreview(objet.id, needsPreview, size);
  const imageUrl = inlineUrl || previewUrl;
  const showMissing = !hasPhotoMeta(objet);
  const showLoading = needsPreview && isLoading && !imageUrl;
  const canOpenViewer = clickable && !!imageUrl;

  const handleClick = (event: React.MouseEvent) => {
    if (!canOpenViewer) return;
    event.stopPropagation();
    onPhotoClick?.(event);
    openPhotoViewer({
      objetId: objet.id,
      title: objet.nom,
      photos: buildPhotoViewerItemsFromObjet(objet),
      startIndex: 0,
    });
  };

  return (
    <div
      className={`flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center ${
        showMissing ? 'bg-amber-50 ring-2 ring-amber-300' : 'bg-gray-100'
      } ${canOpenViewer ? 'cursor-zoom-in ring-0 hover:ring-2 hover:ring-blue-300 transition-shadow' : ''} ${className}`}
      onClick={canOpenViewer ? handleClick : undefined}
      role={canOpenViewer ? 'button' : undefined}
      tabIndex={canOpenViewer ? 0 : undefined}
      onKeyDown={
        canOpenViewer
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClick(event as unknown as React.MouseEvent);
              }
            }
          : undefined
      }
      aria-label={canOpenViewer ? `Voir la photo de ${objet.nom || 'objet'}` : undefined}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={objet.nom || 'Photo objet'}
          className={imgClassName}
          loading="lazy"
          decoding="async"
        />
      ) : showLoading ? (
        <span className="text-gray-300 text-lg animate-pulse">…</span>
      ) : (
        <span className="text-gray-400 text-2xl">{placeholder}</span>
      )}
    </div>
  );
};

export default ObjetPhotoThumb;
